import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, FieldRow, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartySlot } from '../../../parties/index.js';
export const metadata = {
    // Legacy (no `metadata.sections`): this is a one-way letter with no
    // numbered §§, so `field.section` is a plain editor-panel label and the
    // Body renders flowing paragraphs (no <Section> numbering).
    templateKey: 'villkorsandring_54a',
    version: 'v1',
    category: 'Ändring av hyresvillkor',
    title: 'Meddelande om hyreshöjning enligt 12 kap. 54 a § jordabalken',
    shortTitle: 'Hyreshöjning 54 a §',
    titleSuffix: { key: 'apartment_official_number', prefix: 'lgh' },
    formNumber: 'Formulär R-18',
    preamble: 'Hyresvärdens meddelande till hyresgästen om begärd hyreshöjning enligt 12 kap. 54 a § jordabalken.',
    description: 'Används för att höja hyran för lägenhet utan förhandlingsklausul men där en förhandlingsordning gäller för huset (passivitetsregeln).\n\n' +
        'Snäv tillämpning men viktig: gäller endast bostadslägenhet som saknar förhandlingsklausul i hyresavtalet, men där det finns en förhandlingsordning för huset. Om hyresgästen inte skriftligen motsätter sig höjningen inom fristen (minst två månader från avsändandet) blir hen bunden av den nya hyran.\n\n' +
        'Signeras INTE — varken via Scrive eller för hand. Exportera PDF:en och skicka den med rekommenderad post till hyresgästen.',
    status: 'active',
    signingMode: 'export_only',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            // Export-only: no one signs this document.
            signs: false,
        },
        {
            id: 'lessee',
            label: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: false,
        },
    ],
    // Party projection (ADR-0065). Partial: the lessor's contact phone/email
    // sit in the separate "Kontaktuppgifter" section and remain hand-declared.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'Hyresvärd',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number' },
                { attr: 'address', key: 'landlord_address' },
            ],
        },
        {
            slot: 'lessee',
            section: 'Hyresgäst',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_personal_number', label: 'Personnummer' },
                { attr: 'address', key: 'tenant_address' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    // Hyresvärd / Hyresgäst — generated from metadata.partyProjection
    // (ADR-0065). The lessor's contact phone/email live in the separate
    // "Kontaktuppgifter" section below and stay hand-declared.
    ...expandPartySlot(metadata, 'lessor'),
    ...expandPartySlot(metadata, 'lessee'),
    // Lägenheten / fastigheten
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'Lägenheten',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'Lägenheten',
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_address',
        label: 'Postadress',
        type: 'textarea',
        section: 'Lägenheten',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'apartment_official_number',
        label: 'Lägenhetsnummer (officiell)',
        type: 'text',
        section: 'Lägenheten',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    // Hyreshöjning
    {
        key: 'current_rent',
        label: 'Nuvarande hyra (kr/månad)',
        type: 'currency',
        min: 0,
        section: 'Hyreshöjning',
        requiredAtFinal: true,
        // Absolute amount — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'requested_rent',
        label: 'Begärd hyra (kr/månad)',
        type: 'currency',
        min: 0,
        section: 'Hyreshöjning',
        requiredAtFinal: true,
        help: 'Höjningen (skillnaden mot nuvarande hyra) räknas ut automatiskt i meddelandet.',
        scopes: { copy: 'manual' },
    },
    {
        key: 'new_rent_date',
        label: 'Ny hyra gäller fr.o.m.',
        type: 'date',
        section: 'Hyreshöjning',
        requiredAtFinal: true,
        scopes: { copy: 'manual' },
    },
    {
        key: 'objection_deadline',
        label: 'Sista dag att motsätta sig höjningen',
        type: 'date',
        section: 'Hyreshöjning',
        requiredAtFinal: true,
        help: 'Måste vara minst två månader efter den dag då meddelandet avsänds. Hyresgästen blir bunden av den nya hyran om hen inte skriftligen motsätter sig den senast detta datum.',
        scopes: { copy: 'never' },
    },
    // Kontaktuppgifter
    {
        key: 'contact_phone',
        label: 'Telefonnummer',
        type: 'text',
        section: 'Kontaktuppgifter',
        autofill: { kind: 'party', role: 'lessor', ordinal: 0, field: 'phone' },
    },
    {
        key: 'contact_email',
        label: 'E-postadress',
        type: 'text',
        section: 'Kontaktuppgifter',
        autofill: { kind: 'party', role: 'lessor', ordinal: 0, field: 'email' },
    },
];
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556677-8899',
    landlord_address: 'Sveavägen 100\n113 50 Stockholm',
    tenant_name: 'Anna Andersson',
    tenant_personal_number: '19850312-4567',
    tenant_address: 'Götgatan 35\n116 21 Stockholm',
    property_designation: 'Klockaren 5',
    property_municipality: 'Stockholm',
    property_address: 'Götgatan 35\n116 21 Stockholm',
    apartment_official_number: '1101',
    current_rent: 8500,
    requested_rent: 8900,
    new_rent_date: '2026-10-01',
    objection_deadline: '2026-08-31',
    contact_phone: '08-123 45 67',
    contact_email: 'hyror@bostadsagarna.se',
};
const PLACEHOLDER = '____________';
/** Computed rent increase = requested − current, when both are numbers. */
function rentIncrease(values) {
    const { current_rent, requested_rent } = values;
    if (typeof current_rent === 'number' && typeof requested_rent === 'number') {
        return (requested_rent - current_rent).toLocaleString('sv-SE');
    }
    return PLACEHOLDER;
}
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
/** Underlined inline value (or placeholder) used inside the letter prose. */
function Blank({ children }) {
    return _jsx("span", { className: "border-b border-stone-300 font-serif", children: children });
}
export function Body({ values, }) {
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "mb-1 text-[12px] font-semibold uppercase tracking-wide", children: "Bostadsl\u00E4genhet" }), _jsx("p", { className: "mb-1 text-[12.5px] font-semibold", children: "Meddelande om hyresh\u00F6jning" }), _jsx("p", { className: "mb-3 text-[11px] italic text-stone-600", children: "enligt 12 kap. 54 a \u00A7 jordabalken" }), _jsx("p", { className: "mb-4 text-[10px] font-semibold uppercase tracking-wide text-stone-600", children: "Att skickas med rekommenderad post" }), _jsxs("div", { className: "my-3 border border-stone-300", children: [_jsx(FieldRow, { label: "Hyresv\u00E4rd", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Personnr / org.nr", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Hyresv\u00E4rdens adress", children: v(values.landlord_address) }), _jsx(FieldRow, { label: "Hyresg\u00E4st", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnummer", children: v(values.tenant_personal_number) }), _jsx(FieldRow, { label: "Hyresg\u00E4stens adress", children: v(values.tenant_address) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "L\u00E4genhetens adress", children: v(values.property_address) }), _jsx(FieldRow, { label: "L\u00E4genhetsnummer (officiell)", children: v(values.apartment_official_number) })] }), _jsxs("p", { className: "mt-4 text-[10.5px] leading-relaxed", children: ["Hyresv\u00E4rden beg\u00E4r att hyran f\u00F6r l\u00E4genheten h\u00F6js fr\u00E5n", ' ', _jsx(Blank, { children: typeof values.current_rent === 'number'
                            ? num(values.current_rent)
                            : PLACEHOLDER }), ' ', "kronor per m\u00E5nad till", ' ', _jsx(Blank, { children: typeof values.requested_rent === 'number'
                            ? num(values.requested_rent)
                            : PLACEHOLDER }), ' ', "kronor per m\u00E5nad, vilket inneb\u00E4r en h\u00F6jning om", ' ', _jsx(Blank, { children: rentIncrease(values) }), " kronor per m\u00E5nad."] }), _jsxs("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: ["Den nya hyran beg\u00E4rs g\u00E4lla fr\u00E5n och med den", ' ', _jsx(Blank, { children: v(values.new_rent_date) }), "."] }), _jsxs("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: ["Om hyresg\u00E4sten inte senast den", ' ', _jsx(Blank, { children: v(values.objection_deadline) }), " skriftligen mots\u00E4tter sig den beg\u00E4rda hyresh\u00F6jningen blir hyresg\u00E4sten bunden av den nya hyran fr\u00E5n och med den dag som anges ovan."] }), _jsx("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: "Om hyresg\u00E4sten inom angiven tid mots\u00E4tter sig den beg\u00E4rda hyresh\u00F6jningen och parterna d\u00E4refter inte kan komma \u00F6verens, kan hyresv\u00E4rden ans\u00F6ka hos hyresn\u00E4mnden om \u00E4ndring av hyresvillkoren. Hyresn\u00E4mnden pr\u00F6var d\u00E5 sk\u00E4ligheten av den beg\u00E4rda hyran." }), _jsx("p", { className: "mt-4 text-[10.5px] leading-relaxed", children: "Vid fr\u00E5gor om den beg\u00E4rda hyresh\u00F6jningen kan hyresv\u00E4rden kontaktas p\u00E5 f\u00F6ljande s\u00E4tt:" }), _jsxs("div", { className: "mt-2", children: [_jsx(FieldRow, { label: "Telefonnummer", children: v(values.contact_phone) }), _jsx(FieldRow, { label: "E-postadress", children: v(values.contact_email) }), _jsx(FieldRow, { label: "Postadress", children: v(values.landlord_address) })] })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map